import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Input, Select } from "@andorhealth/clientuireact.designsystem";
import { ROLE_PRESETS } from "@/lib/catalog-api";
import { useConfiguration } from "../hooks/useConfiguration";
import { MAX_WAIT_SECONDS } from "../lib/limits";
import { EditingBanner } from "./EditingBanner";

export function StaffTimingStep() {
  const { policyDraft, setPolicyDraft } = useConfiguration();

  const updateTier = (
    index: number,
    patch: Partial<(typeof policyDraft.tiers)[number]>,
  ) => {
    setPolicyDraft((prev) => ({
      ...prev,
      tiers: prev.tiers.map((tier, tierIndex) =>
        tierIndex === index ? { ...tier, ...patch } : tier,
      ),
    }));
  };

  return (
    <Stack spacing={2}>
      <EditingBanner />
      {policyDraft.tiers.map((tier, index) => (
        <Box key={tier.tierOrder} sx={{ border: "1px solid #e4e7ec", p: 2 }}>
          <Stack
            direction="row"
            sx={{ mb: 1.5, justifyContent: "space-between", alignItems: "center" }}
          >
            <Typography sx={{ fontWeight: 700 }}>Step {tier.tierOrder}</Typography>
            {policyDraft.tiers.length > 1 ? (
              <IconButton
                aria-label="Remove tier"
                onClick={() =>
                  setPolicyDraft((prev) => ({
                    ...prev,
                    tiers: prev.tiers
                      .filter((_, tierIndex) => tierIndex !== index)
                      .map((item, tierIndex) => ({
                        ...item,
                        tierOrder: tierIndex + 1,
                      })),
                  }))
                }
              >
                <DeleteOutlineIcon />
              </IconButton>
            ) : null}
          </Stack>
          <Stack spacing={1.5}>
            <Select
              label="Role"
              value={
                ROLE_PRESETS.some((role) => role.code === tier.roleCode)
                  ? tier.roleCode
                  : "CUSTOM"
              }
              options={[
                ...ROLE_PRESETS.map((role) => ({
                  label: role.label,
                  value: role.code,
                })),
                { label: "Other…", value: "CUSTOM" },
              ]}
              onChange={(event) => {
                const value = String(event.target.value);
                if (value === "CUSTOM") {
                  updateTier(index, {
                    roleCode: "",
                    displayName: "",
                  });
                  return;
                }
                const preset = ROLE_PRESETS.find((role) => role.code === value);
                updateTier(index, {
                  roleCode: value,
                  displayName: preset?.label ?? tier.displayName,
                });
              }}
            />
            {!ROLE_PRESETS.some((role) => role.code === tier.roleCode) ? (
              <Input
                size="small"
                label="Role name"
                value={tier.displayName}
                onChange={(event) => {
                  const displayName = event.target.value;
                  updateTier(index, {
                    displayName,
                    roleCode:
                      displayName
                        .trim()
                        .toUpperCase()
                        .replace(/[^A-Z0-9]+/g, "_")
                        .replace(/^_|_$/g, "")
                        .slice(0, 64) || tier.roleCode,
                  });
                }}
              />
            ) : null}
            <Input
              size="small"
              type="number"
              label="Wait (seconds)"
              value={tier.timeoutSeconds}
              onChange={(event) => {
                updateTier(index, {
                  timeoutSeconds: Math.min(
                    MAX_WAIT_SECONDS,
                    Math.max(1, Number(event.target.value) || 1),
                  ),
                });
              }}
              helperText={`1–${MAX_WAIT_SECONDS} seconds (max 1 hour)`}
              slotProps={{
                htmlInput: { min: 1, max: MAX_WAIT_SECONDS, step: 1 },
              }}
            />
          </Stack>
        </Box>
      ))}
      <Button
        variant="outlined"
        onClick={() =>
          setPolicyDraft((prev) => ({
            ...prev,
            tiers: [
              ...prev.tiers,
              {
                tierOrder: prev.tiers.length + 1,
                roleCode: "NURSE",
                timeoutSeconds: 90,
                displayName: "Nurse",
                channels: [],
              },
            ],
          }))
        }
      >
        Add step
      </Button>
    </Stack>
  );
}
